import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import {
  removeAccessTokenFromLocal,
  removeLoginSyncFromLocal,
  removeOrderIdFromLocal,
  removeOpportunityCodeFromLocal,
  markExplicitLogout,
  notifySessionLogoutCompleteAcrossTabs,
  notifySessionLogoutAcrossTabs,
} from "../../utils/common";
import { msalInstance } from "../../msalConfig";

export interface ResponseError {
  message: string;
}

const initialState: {
  isLogout: boolean;
  isLogin: boolean;
  isLoginError: boolean;
  isLogoutProgress: boolean;
} = {
  isLogout: false,
  isLogin: false,
  isLoginError: false,
  isLogoutProgress: false,
};

export const getUserLogout = createAsyncThunk<
  any,
  void,
  { rejectValue: ResponseError }
>("UserLogoutSlice/getUserLogout", async (_, thunkApi) => {
  let logoutSucceeded = false;

  try {
    notifySessionLogoutAcrossTabs();
    const response = await httpClient.post("/logout");
    const successStatusCodes = [200, 201, 202];
    if (
      !successStatusCodes.includes(response.status) ||
      response.data.Status !== "success"
    ) {
      if (response.data.Status === "error") {
        return thunkApi.rejectWithValue({ message: response.data.Message });
      } else {
        return thunkApi.rejectWithValue({ message: "Failed to logout user." });
      }
    } else {
      logoutSucceeded = true;
      markExplicitLogout();
      removeAccessTokenFromLocal();
      removeLoginSyncFromLocal();
      removeOrderIdFromLocal();
      removeOpportunityCodeFromLocal();

      // Remove MSAL accounts and tokens stored in localStorage.
      await msalInstance.clearCache();
    }
  } catch (error) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({ message: "Failed to get user Logout" });
  } finally {
    notifySessionLogoutCompleteAcrossTabs(logoutSucceeded);
  }
});

export const getUserLogin = createAsyncThunk<
  any,
  void,
  { rejectValue: ResponseError }
>("UserLogoutSlice/getUserLogin", async (_, thunkApi) => {
  try {
    const response = await httpClient.post("/login");
    const successStatusCodes = [200, 201, 202];
    if (
      !successStatusCodes.includes(response.status) ||
      response.data.Status !== "success"
    ) {
      if (response.data.Status === "error") {
        return thunkApi.rejectWithValue({ message: response.data.Message });
      } else {
        return thunkApi.rejectWithValue({ message: "Failed to login user." });
      }
    }
  } catch (error) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({ message: "Failed to get user login" });
  }
});

const UserLogoutSlice = createSlice({
  name: "UserLogoutSlice",
  initialState,
  reducers: {
    resetUserLogout: () => {
      return initialState;
    },
    logoutStartedFromAnotherTab: (state) => {
      state.isLogout = false;
      state.isLogin = false;
      state.isLoginError = false;
      state.isLogoutProgress = true;
    },
    logoutFromAnotherTab: (state) => {
      state.isLogout = true;
      state.isLogin = false;
      state.isLoginError = false;
      state.isLogoutProgress = false;
    },
    resetUserLogin: (state) => {
      state.isLogin = true;
      state.isLoginError = false;
      state.isLogout = false;
      state.isLogoutProgress = false;
    },
    resetUserLoginError: (state) => {
      state.isLoginError = true;
      state.isLogin = false;
      state.isLogout = true;
      state.isLogoutProgress = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserLogout.pending, (state) => {
      state.isLogout = false;
      state.isLogoutProgress = true;
    });
    builder.addCase(getUserLogout.fulfilled, (state) => {
      state.isLogout = true;
      state.isLogoutProgress = false;
    });
    builder.addCase(getUserLogout.rejected, (state, action) => {
      console.error("User Logout failed:", action.payload);
      state.isLogout = false;
      state.isLogoutProgress = false;
    });
    builder.addCase(getUserLogin.pending, (state) => {
      state.isLogin = false;
      state.isLoginError = false;
    });
    builder.addCase(getUserLogin.fulfilled, (state) => {
      state.isLogin = true;
      state.isLoginError = false;
    });
    builder.addCase(getUserLogin.rejected, (state, action) => {
      console.error("User LogIn failed:", action.payload);
      state.isLogin = false;
      state.isLoginError = true;
    });
  },
});

export const {
  resetUserLogout,
  logoutStartedFromAnotherTab,
  logoutFromAnotherTab,
  resetUserLogin,
  resetUserLoginError,
} = UserLogoutSlice.actions;

export default UserLogoutSlice.reducer;
